import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslate } from 'ts/common/hooks';
import { useClientBookingExperience } from 'client_react/booking/ClientBookingExperience';
import PaymentOptions from 'client_react/booking/components/PaymentOptions';
import PaymentSummary from 'client_react/booking/components/PaymentSummary';
import { getFullPaymentAmount, getPartialPaymentAmount } from 'client_react/booking/helpers';
import useBookingFlowSteps from 'client_react/booking/hooks/useBookingFlowSteps';
import { IBookingSession } from 'client_react/booking/types';
import {
    isBookingSessionError,
    isBookingSessionRequiresActionResponse
} from 'client_react/booking/useBookingSession';
import ClientPaymentForm from 'client_react/common/components/ClientPaymentForm';
import type { ClientPaymentOption } from 'client_react/common/components/ClientPaymentOptions';
import PaymentTipSection from 'client_react/common/components/PaymentTipSection';

const PaymentForm = ({
    paymentAmountType,
    setPaymentAmountType
}: {
    paymentAmountType: ClientPaymentOption;
    setPaymentAmountType: (x: ClientPaymentOption) => void;
}) => {
    const { bookingSession, isPending, submitPayment } = useClientBookingExperience();

    const { goToPreviousStep, goToNextStep } = useBookingFlowSteps();

    const t = useTranslate('booking.payment');
    const stripe = useStripe();
    const elements = useElements();

    const errorToast = useToast({
        status: 'error'
    });

    const [isReadyToPay, setIsReadyToPay] = useState(false);
    const [tipAmount, setTipAmount] = useState<number | null>(null);

    const showTipSection =
        bookingSession?.isTippingEnabled &&
        (paymentAmountType === 'full' || !bookingSession?.isTipOnlyOnFinalPayment);

    const totalPaymentAmount =
        paymentAmountType === 'full'
            ? getFullPaymentAmount(bookingSession as IBookingSession)
            : getPartialPaymentAmount(bookingSession as IBookingSession);

    useEffect(() => {
        if (paymentAmountType) {
            setTipAmount(0);
        }
    }, [paymentAmountType]);

    const handlePaymentFormReady = useCallback(() => {
        setIsReadyToPay(true);
    }, []);

    const handleTipAmountChange = useCallback(
        (tipAmount: number) => {
            setTipAmount(tipAmount);
        },
        [setTipAmount]
    );

    const handleSubmit = useCallback(
        async (
            event: React.FormEvent | { paymentIntentId: string } | { setupIntentId: string }
        ) => {
            if (!stripe || !elements) {
                return;
            }

            const paymentIntentId = 'paymentIntentId' in event ? event.paymentIntentId : undefined;
            const setupIntentId = 'setupIntentId' in event ? event.setupIntentId : undefined;
            let paymentMethodId: string | undefined;

            if (!paymentIntentId && !setupIntentId) {
                const { error: elementsError } = await elements.submit();

                if (elementsError) {
                    errorToast({
                        title: elementsError.message
                    });
                    return;
                }

                const paymentMethodResponse = await stripe.createPaymentMethod({
                    elements
                });

                if (paymentMethodResponse.error) {
                    errorToast({
                        // TODO Make sure this error message is user-friendly
                        title: paymentMethodResponse.error.message
                    });
                    return;
                }

                paymentMethodId = paymentMethodResponse.paymentMethod.id;
            }

            const submitPaymentResponse = await submitPayment({
                paymentAmountType,
                paymentIntentId,
                paymentMethodId,
                setupIntentId,
                tipAmount
            });

            if (isBookingSessionRequiresActionResponse(submitPaymentResponse)) {
                // This payment intent requires user verification (3D Secure, etc.)
                // So we need to handle that and send the confirmed payment intent id back to our payment endpoint.
                // The API error response detail property is a string that looks like this
                // requires_action:pi_xxxxxxxxxxxxxxxxxxxxxxxx_secret_xxxxxxxxxxxxxxxxxxxxxxxxx
                const clientSecret = submitPaymentResponse.detail.substring(
                    'requires_action:'.length
                );

                if (!(clientSecret.startsWith('pi_') || clientSecret.startsWith('seti_'))) {
                    errorToast({
                        // TODO Show a more specific or accurate error message
                        title: t('error.cardDeclined')
                    });
                    return;
                }

                const { error, paymentIntent, setupIntent } = await stripe.handleNextAction({
                    clientSecret
                });

                if (
                    error ||
                    (paymentIntent && paymentIntent.status !== 'succeeded') ||
                    (setupIntent && setupIntent.status !== 'succeeded')
                ) {
                    errorToast({
                        // TODO Show a more specific or accurate error message
                        title: t('error.cardDeclined')
                    });
                } else if (paymentIntent) {
                    // Resubmit the payment with the confirmed paymentIntentId:
                    await handleSubmit({
                        paymentIntentId: paymentIntent.id
                    });
                } else if (setupIntent) {
                    // Resubmit the payment with the confirmed setupIntentId:
                    await handleSubmit({
                        setupIntentId: setupIntent.id
                    });
                }
            } else if (isBookingSessionError(submitPaymentResponse)) {
                errorToast({
                    // TODO Show a more specific or accurate error message
                    title: t('error.cardDeclined')
                });
            } else {
                goToNextStep();
            }
        },
        [elements, errorToast, goToNextStep, paymentAmountType, stripe, submitPayment, t, tipAmount]
    );

    return (
        <ClientPaymentForm
            backButtonText={t('back')}
            billingInformationFields={<PaymentElement onReady={handlePaymentFormReady} />}
            billingInformationTitle={t('billingInformation')}
            isSubmitDisabled={!stripe || !elements || !isReadyToPay || isPending}
            isSubmitLoading={isPending}
            onClickBack={goToPreviousStep}
            onSubmit={handleSubmit}
            paymentOptions={
                <PaymentOptions
                    paymentAmountType={paymentAmountType}
                    setPaymentAmountType={setPaymentAmountType}
                />
            }
            paymentSummary={
                <PaymentSummary
                    paymentAmountType={paymentAmountType}
                    showTipSection={showTipSection}
                    tipAmount={tipAmount ?? undefined}
                />
            }
            submitButtonText={t('bookSession')}
            tipSection={
                showTipSection ? (
                    <PaymentTipSection
                        currencyCode={bookingSession.currencyCode}
                        tipSuggestion1={bookingSession.tipSuggestion1}
                        tipSuggestion2={bookingSession.tipSuggestion2}
                        tipSuggestion3={bookingSession.tipSuggestion3}
                        totalPaymentAmount={totalPaymentAmount}
                        value={tipAmount ?? 0}
                        onChange={handleTipAmountChange}
                    />
                ) : null
            }
        />
    );
};

export default PaymentForm;
